import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';
import {
  TAB_TYPES,
  CATEGORY_BASE_OPTIONS,
  CATEGORY_TYPE_MAP,
  parseCategoryValue,
  buildCategoryValue
} from './HomeScreenConfigData';

// Red asterisk marker for required fields.
const Req = () => <span style={{ color: '#dc3545' }}> *</span>;

const EMPTY_STATE = {
  tab_type: '',
  seq_id: 1,
  heading: '',
  subtext: '',
  heading_hin: '',
  heading_mar: '',
  heading_guj: '',
  subtext_hin: '',
  subtext_mar: '',
  subtext_guj: '',
  category_base: '',
  category_subcode: '',
  category_desc: '',
  category_id: '',
  container_id: '',
  info_link_video: '',
  active: 1
};

const hydrateFromRow = (row) => {
  if (!row) return { ...EMPTY_STATE };
  const { base, subcode } = parseCategoryValue(row.category);
  return {
    tab_type: row.tab_type || 'Parikramas',
    seq_id: row.seq_id ?? 1,
    heading: row.heading || '',
    subtext: row.subtext || '',
    heading_hin: row.heading_hin || '',
    heading_mar: row.heading_mar || '',
    heading_guj: row.heading_guj || '',
    subtext_hin: row.subtext_hin || '',
    subtext_mar: row.subtext_mar || '',
    subtext_guj: row.subtext_guj || '',
    category_base: base,
    category_subcode: subcode,
    category_desc: row.category_desc || CATEGORY_TYPE_MAP[base] || '',
    category_id: row.category_id || '',
    container_id: row.container_id || '',
    info_link_video: row.info_link_video || '',
    active: row.active ? 1 : 0
  };
};

const buildPayload = (s) => ({
  tab_type: s.tab_type,
  seq_id: Number(s.seq_id),
  heading: s.heading,
  subtext: s.subtext || null,
  heading_hin: s.heading_hin || null,
  heading_mar: s.heading_mar || null,
  heading_guj: s.heading_guj || null,
  subtext_hin: s.subtext_hin || null,
  subtext_mar: s.subtext_mar || null,
  subtext_guj: s.subtext_guj || null,
  category: buildCategoryValue(s.category_base, s.category_subcode),
  category_desc: s.category_desc,
  category_id: s.category_id,
  container_id: s.container_id || null,
  info_link_video: s.info_link_video || null,
  active: s.active ? 1 : 0
});

const validate = (s) => {
  const errors = [];
  if (!TAB_TYPES.includes(s.tab_type)) errors.push('Tab type is required.');
  if (s.seq_id === '' || isNaN(Number(s.seq_id))) errors.push('Seq id must be a number.');
  if (!s.heading?.trim())     errors.push('Heading (English) is required.');
  if (!s.heading_hin?.trim()) errors.push('Heading (Hindi) is required.');
  if (!s.heading_mar?.trim()) errors.push('Heading (Marathi) is required.');
  if (!s.heading_guj?.trim()) errors.push('Heading (Gujarati) is required.');
  if (!s.subtext?.trim())     errors.push('Subtext (English) is required.');
  if (!s.subtext_hin?.trim()) errors.push('Subtext (Hindi) is required.');
  if (!s.subtext_mar?.trim()) errors.push('Subtext (Marathi) is required.');
  if (!s.subtext_guj?.trim()) errors.push('Subtext (Gujarati) is required.');
  if (!s.category_base) errors.push('Category is required.');
  const ids = (s.category_id || '').split(',').map(x => x.trim()).filter(Boolean);
  if (ids.length === 0) errors.push('At least one category_id is required.');
  return { ok: errors.length === 0, errors };
};

const HomeScreenConfigForm = ({ show, onHide, mode, initialRow }) => {
  const isEdit = mode === 'edit';
  const [state, setState] = useState(EMPTY_STATE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setState(initialRow ? hydrateFromRow(initialRow) : { ...EMPTY_STATE });
  }, [show, initialRow]);

  const setField = (name, value) => setState(prev => ({ ...prev, [name]: value }));

  const handleBaseChange = (value) => {
    const opt = CATEGORY_BASE_OPTIONS.find(o => o.value === value);
    setState(prev => ({
      ...prev,
      category_base: value,
      category_subcode: '',
      // Auto-fill desc from the canonical map; admin can still override for legacy values
      // like "live" on code 11 (map says "live-darshan").
      category_desc: opt?.category_desc || ''
    }));
  };

  const parsedIds = useMemo(
    () => (state.category_id || '').split(',').map(x => x.trim()).filter(Boolean),
    [state.category_id]
  );

  const handleSubmit = async () => {
    const { ok, errors } = validate(state);
    if (!ok) {
      errors.forEach(e => toast.error(e));
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(state);
      let res;
      if (isEdit) {
        res = await _axios.put(`${ServerPath}/home-screen-config/admin/${initialRow.id}`, payload);
      } else {
        res = await _axios.post(`${ServerPath}/home-screen-config/admin`, payload);
      }
      if (res.data?.success) {
        toast.success(isEdit ? 'Row updated.' : 'Row created.');
        onHide(true);
      } else {
        toast.error(res.data?.message || 'Save failed');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={() => onHide(false)} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? `Edit Home Screen Configuration` : 'Create Home Screen Configuration'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Section */}
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <Form.Label>Tab type<Req /></Form.Label>
              <Form.Select value={state.tab_type} onChange={(e) => setField('tab_type', e.target.value)}>
                <option value="" disabled hidden>Select a tab type</option>
                {TAB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </div>
            <div className="col-md-4">
              <Form.Label>Seq id<Req /></Form.Label>
              <Form.Control
                type="number"
                value={state.seq_id}
                onChange={(e) => setField('seq_id', e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Status<Req /></Form.Label>
              <Form.Select
                value={state.active ? '1' : '0'}
                onChange={(e) => setField('active', e.target.value === '1' ? 1 : 0)}
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </Form.Select>
            </div>
          </div>

          {/* Heading — English / Hindi / Marathi / Gujarati side-by-side */}
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <Form.Label>Heading (English)<Req /></Form.Label>
              <Form.Control
                type="text"
                value={state.heading}
                onChange={(e) => setField('heading', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <Form.Label>Heading (Hindi)<Req /></Form.Label>
              <Form.Control
                type="text"
                value={state.heading_hin}
                onChange={(e) => setField('heading_hin', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <Form.Label>Heading (Marathi)<Req /></Form.Label>
              <Form.Control
                type="text"
                value={state.heading_mar}
                onChange={(e) => setField('heading_mar', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <Form.Label>Heading (Gujarati)<Req /></Form.Label>
              <Form.Control
                type="text"
                value={state.heading_guj}
                onChange={(e) => setField('heading_guj', e.target.value)}
              />
            </div>
          </div>

          {/* Subtext — English / Hindi / Marathi / Gujarati side-by-side */}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <Form.Label>Subtext (English)<Req /></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={state.subtext}
                onChange={(e) => setField('subtext', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Subtext (Hindi)<Req /></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={state.subtext_hin}
                onChange={(e) => setField('subtext_hin', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Subtext (Marathi)<Req /></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={state.subtext_mar}
                onChange={(e) => setField('subtext_mar', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Subtext (Gujarati)<Req /></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={state.subtext_guj}
                onChange={(e) => setField('subtext_guj', e.target.value)}
              />
            </div>
          </div>

          {/* Content mapping */}
          <div className="row g-3 mb-2">
            <div className="col-md-4">
              <Form.Label>Category<Req /></Form.Label>
              <Form.Select
                value={state.category_base}
                onChange={(e) => handleBaseChange(e.target.value)}
              >
                <option value="" disabled hidden>Select a category</option>
                {CATEGORY_BASE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-2">
              <Form.Label>Subcode</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. 17"
                disabled={!state.category_base}
                value={state.category_subcode}
                onChange={(e) => setField('category_subcode', e.target.value.replace(/[^0-9]/g, ''))}
              />
              <Form.Text muted>
                Final: <code>{buildCategoryValue(state.category_base, state.category_subcode) || '—'}</code>
              </Form.Text>
            </div>
            <div className="col-md-6">
              <Form.Label>Category description</Form.Label>
              <Form.Control
                type="text"
                value={state.category_desc}
                onChange={(e) => setField('category_desc', e.target.value)}
                placeholder="e.g. parikrama, read, live"
              />
              <Form.Text muted>Auto-fills from category; editable for legacy variants (e.g. code 11 → "live").</Form.Text>
            </div>
          </div>

          <Form.Label>Category IDs<Req /> <span className="text-muted small">(comma-separated)</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={state.category_id}
            onChange={(e) => setField('category_id', e.target.value)}
            placeholder="e.g. 24,25,26 or 0000003266,0000003298 or WAYPT-1000700,WAYPT-1000701"
          />
          {parsedIds.length > 0 && (
            <div className="mt-2 d-flex flex-wrap gap-1">
              {parsedIds.map((id, i) => (
                <span key={i} className="badge bg-light text-dark border">{id}</span>
              ))}
              <span className="text-muted small ms-2">{parsedIds.length} ID{parsedIds.length > 1 ? 's' : ''}</span>
            </div>
          )}

          <div className="row g-3 mt-2">
            <div className="col-md-6">
              <Form.Label>Container ID</Form.Label>
              <Form.Control
                type="text"
                value={state.container_id}
                onChange={(e) => setField('container_id', e.target.value)}
                placeholder="e.g. LCONT00001"
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Preview video URL</Form.Label>
              <Form.Control
                type="url"
                value={state.info_link_video}
                onChange={(e) => setField('info_link_video', e.target.value)}
              />
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : (isEdit ? 'Update' : 'Create')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HomeScreenConfigForm;
